import React, {
  BaseSyntheticEvent,
  useMemo,
  useEffect,
  useState,
  use,
} from 'react';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Text } from '@uhg-abyss/web/ui/Text';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { useAtomValue } from 'jotai';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';
import { UserInfo, FindhelpReferralRequest, FindhelpProgram, Need } from '@src/api.types';
import { loggedInAdvocateAtom, memberInContextAtom } from '@src/global-state';
import { formDataToCustomCBORequest, type CustomFormData } from './CustomFormHelpers';
import { API } from '@src/api';

export type CboCustomFormProps = {
  setReferResponse: (value: any) => void;
  onClose: () => void;
  setIsResultsModalOpen?: (value: boolean) => void;
};

const PRIMARY_NEED_OPTIONS = [
  { label: 'Food', value: 'Food' },
  { label: 'Clothing', value: 'Clothing' },
  { label: 'Utilities', value: 'Utilities' },
  { label: 'Transportation', value: 'Transportation' },
  { label: 'Social Support', value: 'Social Support' },
  { label: 'Housing', value: 'Housing' },
  { label: 'Internet', value: 'Internet' },
  { label: 'Computer/Tablet', value: 'Computer/Tablet' },
  { label: 'Work', value: 'Work' },
  { label: 'Legal', value: 'Legal' },
  { label: 'Medical Cost', value: 'Medical Cost' },
  { label: 'Phone', value: 'Phone' },
  { label: 'Social Environment', value: 'Social Environment' },
  { label: 'Justice System Support', value: 'Justice System Support' },
  { label: 'Family Support', value: 'Family Support' },
  { label: 'Health', value: 'Health' },
  { label: 'Employment', value: 'Employment' },
  { label: 'Education', value: 'Education' },
] as const;

export function CboCustomForm({
  setReferResponse,
  onClose,
  setIsResultsModalOpen
}: CboCustomFormProps): React.JSX.Element {
  const [isConsent, setIsConsent] = useState(true);
  const [initialCheck, setinitialCheck] = useState(true);
  const [showConsentPermission, setshowConsentPermission] = useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const featureFlags = getFeatureFlagsFromStorage();
  const memberInContext = useAtomValue(memberInContextAtom);
  const loggedInAdvocate = useAtomValue(loggedInAdvocateAtom);
  const secureRole = 'Member Services - Med Supp - National - Clinical 24_7 Nurses using ICUE'; // TESTING: 'UHCCC_Dev_Tech_Team'
  const form = useForm<CustomFormData>({
    defaultValues: {
      advocateFirstName: loggedInAdvocate?.first_name || '',
      advocateLastName: loggedInAdvocate?.last_name || '',
      advocateEmail: loggedInAdvocate?.email || '',
      memberFirstName: memberInContext?.first_name || '',
      memberLastName: memberInContext?.last_name || '',
      memberEmail: memberInContext?.email || '',
      memberPhone: memberInContext?.phone || '',
      confirmConsent: false,
      contactPreferences: [],
      primaryNeed: [],
      programName: '',
      organizationName: '',
      consent: false,
      ConfirmPermission: false,
      pennsylvaniaCoalitionConsent: null,
    },
    mode: 'onChange',
  });

  useEffect(() => {
      setshowConsentPermission(
        loggedInAdvocate?.roles?.includes(secureRole) || false
      );
    }, [loggedInAdvocate]);

  const handleCheckboxChange = (e: BaseSyntheticEvent) => {
    const { model, checked } = e.target;
    if (model === 'consent') {
      // Handle the "Confirm Consent" checkbox
      if (!checked) {
        form.setValue('consent', false);
        form.clearErrors('consent');
        setinitialCheck(true);
      } else {
        form.setValue('consent', true);
        form.trigger('consent'); // Trigger validation
        setinitialCheck(false);
      }
    } else if (model === 'ConfirmPermission') {
      // Handle the "AARP Med Supp Confirm Consent" checkbox
      if (!checked) {
        form.setValue('ConfirmPermission', false);
        form.clearErrors('ConfirmPermission');
        setinitialCheck(true);
      } else {
        form.setValue('ConfirmPermission', true);
        form.trigger('ConfirmPermission'); // Trigger validation
        setinitialCheck(false);
        }
    }
  };

  const handleSubmit = () => {
    const values = form.getValues();
    onSubmit(values);
  };

  async function onSubmit(data: CustomFormData): Promise<any> {
    if (data.ConfirmPermission) {
      delete data['ConfirmPermission'];
      data.consent = true;

    }

    const req = formDataToCustomCBORequest(
      data,
      memberInContext!,
      loggedInAdvocate!
    );

    setIsLoading(true);

    if (!API?.submitReferralCustom) {
      console.error('API.submitReferralCustom is not defined');
      return;
    }

    const resp = await API.submitReferralCustom(
      req as FindhelpReferralRequest

    );
    if (resp === null) {
      console.warn(
        'Referral response was null, this could indicate an error.'
      );
      setReferResponse(null);
    } else if (resp === undefined) {
      console.error(
        'Referral response was undefined, this definitely indicates an error.'
      );
      setReferResponse(undefined);
    } else {
      setReferResponse(resp);
    }
    setIsLoading(false);
    onClose();
    if (setIsResultsModalOpen) {
      setIsResultsModalOpen(true); // Updates Submission Results modal state to open
    }
  }

  return (
    <div style={{ padding: '10px' }}>
      <FormProvider state={form} onSubmit={handleSubmit}>
        <Grid>
          {/* Organization and Program Section */}
          <Grid.Col span="100%">
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <TextInput
                label="Organization Name"
                model="organizationName"
                onChange={(e) => {return form.setValue('organizationName', e.target.value, { shouldValidate: true })}}
                onBlur={() => form.trigger('organizationName')}
                placeholder="Organization Name"
                validators={{ required: 'Organization Name is required' }}
                data-cy="custom-ref-form-organization-name"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextInput
                label="Program Name"
                model="programName"
                onChange={(e) => {return form.setValue('programName', e.target.value)}}
                placeholder="Program Name"
                validators={{ required: false }}
                data-cy="custom-ref-form-program-name"
              />
            </div>
          </div>
          <p style={{ color: 'grey', marginTop: '10px' }}>
            This CBO is not part of the UHC/findhelp network.
          </p>
          </Grid.Col>

        <PaddedLineBreak />

          {/* Advocate Information Section */}
          <Grid.Col span="100%">
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="Your First Name"
                  model="advocateFirstName"
                  isDisabled
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="Your Last Name"
                  model="advocateLastName"
                  isDisabled
                />
              </div>
            </div>
            <div style={{ width: 'calc(50% - 5px)', marginTop: '10px' }}>
              <TextInput
                label="Your Email"
                model="advocateEmail"
                isDisabled
                data-cy="custom-ref-form-advocate-email"
              />
            </div>
          </Grid.Col>

          <PaddedLineBreak />

            {/* Member Information Section */}
            <Grid.Col span="100%">
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
              <TextInput
                label="Member's First Name"
                model="memberFirstName"
                placeholder="First Name"
                validators={{ required: true }}
              />
              </div>
              <div style={{ flex: 1 }}>
              <TextInput
                label="Member's Last Name"
                model="memberLastName"
                placeholder="Last Name"
                validators={{ required: true }}
              />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
              <TextInput
                label="Member's Phone"
                model="memberPhone"
                placeholder="Phone"
                mask="phone"
                validators={{ required: false }}
              />
              </div>
              <div style={{ flex: 1 }}>
              <TextInput
                label="Member's Email"
                model="memberEmail"
                placeholder="Email"
                validators={{
                required: false,
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email address',
                },
                }}
              />
              </div>
            </div>
            <div style={{ marginTop: '10px', width: 'calc(50% - 5px)' }}>
              <SelectInput
              label="Primary Need"
              model="primaryNeed"
              options={[...PRIMARY_NEED_OPTIONS]}
              placeholder="Select Primary Need"
              validators={{ required: true }}
              onChange={() => form.trigger('primaryNeed')}
              onBlur={() => form.trigger('primaryNeed')}
              />
            </div>
            </Grid.Col>

          <PaddedLineBreak />

          {/* Consent Section */}

          <Grid.Col span="100%">
            <div style={{ marginBottom: '10px' }}>
              <p>
              Submit the referral form to contribute to our referral program and enable essential follow-up action to support the member.
              </p>
            </div>

            <div className="paddingTop10px">
              <Checkbox
              label="Confirm Consent"
              model="consent"
              onChange={handleCheckboxChange}
              validators={{ required: 'Consent is required' }}
              data-cy="custom-referral-form-consent-checkbox"
              />
            </div>

            <div className="paddingTop10px">
              <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
                <li>
                  Record these referral details along with Member's name, identified
                  social need(s), contact information, and any notes in the system,
                  so that UHC can follow up with the member.
                </li>
              </ul>
            </div>
          </Grid.Col>

          <PaddedLineBreak />

          {/* Med Supp Consent Section */}
          {showConsentPermission && (
            <Grid.Col span="100%">

              <div className="paddingTop10px">
                <Checkbox
                  label="AARP Med Supp Confirm Consent"
                  model="ConfirmPermission"
                  onChange={handleCheckboxChange}
                  validators={{ required: 'Med Supp Consent is required' }}

                />
              </div>

              <div className="paddingTop10px">
                <p>
                  I have verbal consent from the member (or the member’s personal
                  representative) to research community resources and record referral details in the
                  system so that UHC can follow up with the member.
                </p>
              </div>
            </Grid.Col>
          )}

          {/* Submit Button */}
          <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Button
              type="submit"
              isDisabled={!form.formState.isValid}
              data-cy="custom-referral-form-refer-button"
            >
              Refer
            </Button>
          </div>
        </Grid>
      </FormProvider>
      {isLoading ? (
        <div
          style={{
            zIndex: 999,
            backgroundColor: '#FFFFFF',
            opacity: 0.75,
            position: 'fixed',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            top: 0,
            left: 0,
          }}
        >
          <LoadingSpinner
            size="xl"
            isLoading={isLoading}
            ariaLoadingLabel="Referring...."
          />
        </div>
      ) : (
        ''
      )}

    </div>
  );
}

const PaddedLineBreak = () => {
  return (
    <hr
      style={{
        marginLeft: '16px',
        marginRight: '16px',
        width: '100%',
        marginTop: '16px',
        marginBottom: '8px',
      }}
    />
  );
};